import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useQuery, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import WorkspaceSelector from './WorkspaceSelector';
import SubscriptionBadge from './SubscriptionBadge';
import SubscriptionBanner from './SubscriptionBanner';
import WhatsAppSetupBanner from './WhatsAppSetupBanner';
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  Users,
  Mail,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Workflow,
  Building2,
  Key,
  Bot,
  ChevronDown,
  CreditCard,
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Fetch user permissions for menu visibility
  // Cache for 2 min to reduce DB connection churn (max_connections_per_hour limit on shared hosting)
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery(
    ['userPermissions', currentWorkspace?.id, user?.id],
    async () => {
      if (!currentWorkspace?.id || !user?.id) return null;
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get(
        `/api/workspaces/${currentWorkspace.id}/users/me/permissions`,
        { headers }
      );
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id && !!user?.id,
      retry: 1,
      staleTime: 2 * 60 * 1000, // 2 min - reduce permission API calls
      cacheTime: 5 * 60 * 1000, // 5 min cache
      refetchOnMount: true,
      refetchOnWindowFocus: false, // Was causing excessive refetches on tab switch
    }
  );

  // Refetch permissions when workspace changes
  useEffect(() => {
    if (currentWorkspace?.id && user?.id) {
      queryClient.invalidateQueries(['userPermissions']);
    }
  }, [currentWorkspace?.id, user?.id, queryClient]);

  // Auto-expand dropdown when on a child route
  const path = location.pathname;
  useEffect(() => {
    if (['/templates', '/flows', '/whatsapp-statistics', '/whatsapp-logs', '/api-sharing'].includes(path)) setWhatsappOpen(true);
    if (['/email-templates', '/email-campaigns', '/statistics'].includes(path)) setEmailOpen(true);
  }, [path]);

  const userPermissions = permissionsData?.permissions || [];
  const userRole = permissionsData?.role;

  /** Account-level areas: only workspace owner or workspace admin — not members/agents with settings.view */
  const isWorkspaceOwnerOrAdmin = () => {
    const r = String(userRole || '').toLowerCase();
    return r === 'owner' || r === 'admin';
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Flat items and dropdown groups
  const flatItems = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'dashboard.view' },
    { name: 'Chats', href: '/chats', icon: MessageSquare, permission: 'chats.view' },
    { name: 'Chatbot', href: '/chatbot', icon: Bot, permission: 'chats.view' },
    { name: 'Contacts', href: '/contacts', icon: Users, permission: 'contacts.view' },
    { name: 'Leads Analysis', href: '/analytics', icon: BarChart3, permission: 'dashboard.view' },
  ];



  const whatsappDropdown = {
    label: 'WhatsApp Config',
    icon: MessageSquare,
    children: [
      { name: 'Templates', href: '/templates', icon: FileText, permission: 'templates.view' },
      { name: 'Campaigns', href: '/flows', icon: Workflow, permission: 'campaigns.view' },
      { name: 'Statistics', href: '/whatsapp-statistics', icon: BarChart3, permission: 'whatsapp_stats.view' },
      { name: 'Logs', href: '/whatsapp-logs', icon: FileText, permission: 'whatsapp_stats.view' },
      { name: 'API Sharing', href: '/api-sharing', icon: Key, permission: 'api.view' },
    ],
  };

  const emailDropdown = {
    label: 'Email Config',
    icon: Mail,
    children: [
      { name: 'Templates', href: '/email-templates', icon: Mail, permission: 'email_templates.view' },
      { name: 'Campaigns', href: '/email-campaigns', icon: Workflow, permission: 'email_campaigns.view' },
      { name: 'Statistics', href: '/statistics', icon: BarChart3, permission: 'email_stats.view' },
    ],
  };

  const bottomItems = [
    { name: 'Billing & Invoice', href: '/billing', icon: CreditCard, permission: 'settings.view', ownerOrAdminOnly: true },
    { name: 'Workspace', href: '/workspace', icon: Building2, permission: 'workspace.view' },
    { name: 'Workspace Members', href: '/workspace/users', icon: Users, permission: 'workspace.manage_users' },
    { name: 'Settings', href: '/settings', icon: Settings, permission: 'settings.view', ownerOrAdminOnly: true },
  ];

  const hasPermission = (permission) => {
    // No workspace selected (e.g. renewing plan): don't expose feature pages
    if (!currentWorkspace?.id) return false;
    if (permissionsLoading || !permissionsData) return false;
    if (userRole === 'owner' || userRole === 'admin') return true;
    return userPermissions.includes(permission);
  };

  const canShowBottomItem = (item) => {
    // Allow Billing when no workspace so expired users can renew + still use the shell
    if (!currentWorkspace?.id) {
      return item.href === '/billing';
    }
    if (item.ownerOrAdminOnly && !isWorkspaceOwnerOrAdmin()) return false;
    return hasPermission(item.permission);
  };

  const filterVisibleChildren = (children) =>
    children.filter((c) => {
      if (c.ownerOrAdminOnly && !isWorkspaceOwnerOrAdmin()) return false;
      return hasPermission(c.permission);
    });

  const hasAnyChild = (dropdown) =>
    filterVisibleChildren(dropdown.children).length > 0;

  /** Match route hrefs; supports query (e.g. /settings?tab=whatsapp). */
  const isActive = (href) => {
    if (!href) return false;
    const [pathname, queryString] = href.split('?');
    if (location.pathname !== pathname) return false;
    if (!queryString) return true;
    const want = new URLSearchParams(queryString);
    const have = new URLSearchParams(location.search.slice(1) || '');
    for (const [k, v] of want.entries()) {
      if (have.get(k) !== v) return false;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-[72px] px-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-md">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-xl font-heading font-bold text-white">Leads Automater</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-white/90 hover:text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-4 flex-1 overflow-y-auto">
          <div className="space-y-1.5">
            {permissionsLoading && currentWorkspace?.id ? (
              // Show loading skeleton while permissions are loading
              <div className="space-y-1.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-100 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <>
                {/* Flat items */}
                {flatItems.filter((i) => hasPermission(i.permission)).map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${active
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      <span className={active ? 'font-semibold' : ''}>{item.name}</span>
                    </Link>
                  );
                })}



                {/* WhatsApp config dropdown */}
                {hasAnyChild(whatsappDropdown) && (
                  <div className="space-y-0.5">
                    <button
                      onClick={() => setWhatsappOpen(!whatsappOpen)}
                      className={`w-full group flex items-center justify-between px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${whatsappOpen ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'}`}
                    >
                      <div className="flex items-center">
                        <MessageSquare className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-600" />
                        <span>WhatsApp config</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${whatsappOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {whatsappOpen && filterVisibleChildren(whatsappDropdown.children).map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center pl-11 pr-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${active
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <Icon className={`mr-3 h-4 w-4 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                          <span className={active ? 'font-semibold' : ''}>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}

                {/* Email campaign config dropdown */}
                {hasAnyChild(emailDropdown) && (
                  <div className="space-y-0.5">
                    <button
                      onClick={() => setEmailOpen(!emailOpen)}
                      className={`w-full group flex items-center justify-between px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${emailOpen ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'}`}
                    >
                      <div className="flex items-center">
                        <Mail className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-600" />
                        <span>Emails Config</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${emailOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {emailOpen && filterVisibleChildren(emailDropdown.children).map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center pl-11 pr-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${active
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <Icon className={`mr-3 h-4 w-4 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                          <span className={active ? 'font-semibold' : ''}>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}

                {/* Bottom items */}
                {bottomItems.filter((i) => canShowBottomItem(i)).map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${active
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      <span className={active ? 'font-semibold' : ''}>{item.name}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </div>
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md shadow-lg border-b border-gray-200/50">
          <div className="flex items-center justify-between h-[72px] px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Workspace Selector */}
              <div className="ml-2">
                <WorkspaceSelector />
              </div>

              {/* Subscription Badge */}
              <SubscriptionBadge />
            </div>

            <div className="flex items-center gap-3">
              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 transition-all duration-200 group"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md ring-2 ring-blue-100 group-hover:ring-blue-200 transform group-hover:scale-105 transition-all duration-200">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="hidden sm:block text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {user?.name}
                  </span>
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50 animate-scale-in">
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600">
                      <p className="text-sm font-semibold text-white">{user?.name}</p>
                      <p className="text-xs text-blue-100">{user?.email}</p>
                    </div>
                    <div className="p-2 space-y-1">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200 group"
                      >
                        <LogOut className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Subscription banner (trial/failed/limit) */}
        <SubscriptionBanner />

        {/* Platform admin viewing another owner's personal workspace */}
        {currentWorkspace?.is_platform_admin_view && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 lg:px-8 py-2.5">
            <p className="text-sm text-amber-900">
              Viewing personal workspace of{' '}
              <span className="font-semibold">
                {currentWorkspace.creator_name || currentWorkspace.creator_email || 'user'}
              </span>
              {currentWorkspace.creator_email && currentWorkspace.creator_name ? (
                <span className="text-amber-700"> ({currentWorkspace.creator_email})</span>
              ) : null}
              . You are signed in as platform admin.
            </p>
          </div>
        )}

        {/* WhatsApp / Meta API setup nudge (owner/admin) */}
        <WhatsAppSetupBanner />

        {/* Page content */}
        <main className="min-h-[92vh] p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
